import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { RouterConstant } from '../../../routers/index';
import { useUserStore } from '../../../utils/userStore';
import { useGamificationStore } from '../../../utils/gamificationStore';
import { useNotificationStore } from '../../../utils/notificationStore';
import { useLayoutContext } from '../../../layouts/general-layout';
import {
  headerFlameIcon as flameIcon,
  headerStarIcon as starIcon,
  headerTrophyIcon as trophyIcon,
  headerMenuIcon as menuIcon
} from '../../../assets/images/index';
import './header.scss';

interface V2HeaderProps {
  progressPercentage?: number;
}

export const V2Header: React.FC<V2HeaderProps> = ({ progressPercentage }) => {
  const { totalPoints, currentStreak, levelKey, levelLabel, classMedian, initState } = useGamificationStore();
  const unreadCount = useNotificationStore((state) => state.unreadCount);
  const { t, toggleMenu } = useLayoutContext();

  React.useEffect(() => {
    initState();
  }, [initState]);
  const navigate = useNavigate();
  const { ROUTERS_PATH } = RouterConstant;
  const userInfo = useUserStore((state) => state.userInfo);
  const user_attributes = useUserStore((state) => state.user_attributes);
  const { isAdmin, isTeacher } = useUserStore();

  const fullName = `${userInfo?.first_name || ''} ${userInfo?.last_name || ''}`.trim();

  return (
    <header className="v2-header">
      <div className="v2-header__container">
        <div className="v2-header__top-row">
          <div className="v2-header__start-group">
            <button className="v2-header__menu-btn" onClick={toggleMenu} aria-label="menu">
              <img src={menuIcon} alt="menu" />
            </button>

            <div className="v2-header__pills">
              <Tooltip title={t('headerPillFlameTooltip')} enterTouchDelay={0} arrow placement="bottom">
                <div className="v2-header__pill v2-header__pill--flame">
                  <img src={flameIcon} alt="streak" className="v2-header__pill-icon" />
                  <span className="v2-header__pill-text">
                    {currentStreak > 0 ? currentStreak : 0}
                  </span>
                </div>
              </Tooltip>
              <Tooltip title={t('headerPillStarTooltip')} enterTouchDelay={0} arrow placement="bottom">
                <div className="v2-header__pill v2-header__pill--star">
                  <img src={starIcon} alt="star" className="v2-header__pill-icon" />
                  <span className="v2-header__pill-text">
                    {totalPoints > 0 ? totalPoints : 0}
                  </span>
                </div>
              </Tooltip>
              <Tooltip 
                title={levelLabel ? `${t('headerPillTrophyTooltip')}: ${levelLabel}` : t('headerPillTrophyTooltip')} 
                enterTouchDelay={0} 
                arrow 
                placement="bottom"
              >
                <div className="v2-header__pill v2-header__pill--trophy">
                  <img src={trophyIcon} alt="trophy" className="v2-header__pill-icon" />
                  <span className="v2-header__pill-text">
                    {t(`gamification.level_short.${levelKey}`, levelLabel || t(`gamification.level.${levelKey}`))}
                  </span>
                </div>
              </Tooltip>
              {classMedian >= 5 && (
                <Tooltip title={t('headerPillMedianTooltip')} enterTouchDelay={0} arrow placement="bottom">
                  <div className="v2-header__pill v2-header__pill--median">
                    <span className="v2-header__pill-icon v2-header__pill-icon--emoji">📊</span>
                    <span className="v2-header__pill-text">{classMedian}</span>
                  </div>
                </Tooltip>
              )}
            </div>
          </div>

          <div className="v2-header__actions">
            <Tooltip title={t('notificationCenter')} enterTouchDelay={0} arrow placement="bottom">
              <div
                className="v2-header__pill v2-header__pill--notifications"
                onClick={() => navigate(ROUTERS_PATH.NOTIFICATIONS)}
                role="button"
                tabIndex={0}
              >
                <MailOutlineIcon className="v2-header__pill-icon v2-header__pill-icon--mui" />
                {unreadCount > 0 && (
                  <span className="v2-header__notification-badge">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </div>
            </Tooltip>
          </div>
        </div>
      </div>
      
      {progressPercentage !== undefined && (
        <div className="v2-header__progress-wrapper">
          <span className="v2-header__progress-percent">
            {progressPercentage}%
          </span>
          <div className="v2-header__progress-bar">
            <div 
              className="v2-header__progress-fill" 
              style={{ width: `${Math.min(100, Math.max(0, progressPercentage))}%` }}
            />
          </div>
          <span className="v2-header__progress-label">
            {t('progress')}
          </span>
        </div>
      )}
    </header>
  );
};

export default V2Header;
